import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { validateAngle } from '../utils/validation';

/**
 * Interface for custom angle
 */
export interface CustomAngle {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
}

// API endpoints
const API_BASE_URL = getApiBaseUrl();
const CUSTOM_ANGLES_API = `${API_BASE_URL}/api/custom-angles`;

/**
 * Retrieves all custom contextual value angles for the user
 * @param userId - The ID of the user whose angles to fetch
 * @returns {Promise<string[]>} Array of custom angle names
 */
export const getCustomAngles = async (userId?: string): Promise<string[]> => {
  try {
    const response = await axios.get(`${CUSTOM_ANGLES_API}/${userId || 'default'}`);
    return response.data.angles || [];
  } catch (error) {
    console.error('Error fetching custom angles:', error);
    throw new Error('Failed to fetch custom angles');
  }
};

/**
 * Saves a new custom contextual value angle
 * @param angle - The angle to save
 * @param userId - The ID of the user to save the angle for
 * @returns {Promise<{success: boolean; message: string}>} Result of the operation
 */
export const saveCustomAngle = async (angle: string, userId?: string): Promise<{ success: boolean; message: string }> => {
  // Validate the angle
  const validation = validateAngle(angle);
  if (!validation.isValid) {
    return { success: false, message: validation.message || 'Invalid angle' };
  }

  try {
    const response = await axios.post(CUSTOM_ANGLES_API, {
      angle,
      userId: userId || 'default'
    });
    
    return { success: true, message: 'Custom angle saved successfully' };
  } catch (error) {
    console.error('Error saving custom angle:', error);
    return { success: false, message: 'Failed to save custom angle' };
  }
};

/**
 * Deletes a custom contextual value angle
 * @param {string} angle - The angle to delete
 * @param {string} userId - The ID of the user who owns the angle
 * @returns {Promise<{success: boolean, message: string}>} Result of the operation
 */
export const deleteCustomAngle = async (angle: string, userId?: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(`${CUSTOM_ANGLES_API}/${encodeURIComponent(angle)}`, {
      data: { userId: userId || 'default' }
    });
    
    return { success: true, message: 'Custom angle deleted successfully' };
  } catch (error) {
    console.error('Error deleting custom angle:', error);
    return { success: false, message: 'Failed to delete custom angle' };
  }
};

export default {
  getCustomAngles,
  saveCustomAngle,
  deleteCustomAngle
}; 